import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { personalItemService, UniqueBuyableItem, BuyableItemsParams } from '../services/personalItemService';
import { shipService } from '../services/shipService';
import sharedStyles from '../styles/shared.module.css';
import styles from '../styles/ItemDatabase.module.css';
import { Search, X, ImageOff, Filter, ChevronDown, ChevronUp, Store, DollarSign, MapPin, ShoppingBag, Tag } from 'lucide-react';

// Cache for item images
const imageCache: Record<string, string | null> = {};

// Primary category definitions with their subcategories
const CATEGORY_STRUCTURE: Record<string, { label: string; color: string; subcategories: string[] }> = {
  'Armor': {
    label: 'Armor',
    color: '#3b82f6',
    subcategories: ['Helmet', 'Core', 'Arms', 'Legs', 'Backpack']
  },
  'Undersuit': {
    label: 'Undersuit',
    color: '#6366f1',
    subcategories: []
  },
  'Clothing': {
    label: 'Clothing',
    color: '#a855f7',
    subcategories: ['Jacket', 'Shirt', 'Pants', 'Shoes', 'Hat', 'Gloves']
  },
  'FPS Weapon': {
    label: 'FPS Weapons',
    color: '#ef4444',
    subcategories: ['Pistol', 'Rifle', 'SMG', 'Shotgun', 'Sniper', 'LMG']
  },
  'Consumable': {
    label: 'Consumables',
    color: '#f97316',
    subcategories: ['Food', 'Drink']
  },
  'Medical': {
    label: 'Medical',
    color: '#22c55e',
    subcategories: ['MedPen', 'MedGun', 'Canister']
  },
  'Tool': {
    label: 'Tools',
    color: '#eab308',
    subcategories: ['MultiTool', 'Mining', 'Tractor Beam', 'Repair']
  }
};

// Get color based on primary category
const getCategoryColor = (category?: string) => {
  if (!category) return { bg: 'rgba(107, 114, 128, 0.1)', border: 'rgba(107, 114, 128, 0.3)', accent: '#6b7280' };
  
  for (const [key, config] of Object.entries(CATEGORY_STRUCTURE)) {
    if (category.startsWith(key)) {
      return {
        bg: `${config.color}15`,
        border: `${config.color}40`,
        accent: config.color
      };
    }
  }
  return { bg: 'rgba(107, 114, 128, 0.1)', border: 'rgba(107, 114, 128, 0.3)', accent: '#6b7280' };
};

// Item image component
const ItemImage: React.FC<{ itemName: string; accentColor: string }> = ({ itemName, accentColor }) => {
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    const fetchImage = async () => {
      if (imageCache[itemName] !== undefined) {
        setImageUrl(imageCache[itemName]);
        setLoading(false);
        return;
      }

      try {
        const wikiTitle = itemName.replace(/ /g, '_');
        const response = await fetch(
          `https://starcitizen.tools/api.php?action=query&format=json&titles=${encodeURIComponent(wikiTitle)}&prop=pageimages&pithumbsize=300&origin=*`
        );
        const data = await response.json();
        const pages = data.query?.pages;
        
        if (pages) {
          const pageData = Object.values(pages)[0] as any;
          if (pageData?.thumbnail?.source) {
            imageCache[itemName] = pageData.thumbnail.source;
            setImageUrl(pageData.thumbnail.source);
          } else {
            imageCache[itemName] = null;
          }
        } else {
          imageCache[itemName] = null;
        }
      } catch (err) {
        imageCache[itemName] = null;
      }
      setLoading(false);
    };

    fetchImage();
  }, [itemName]);

  if (loading) {
    return (
      <div className={styles.imageContainer}>
        <div className={styles.imageLoader} style={{ borderColor: accentColor, borderTopColor: 'transparent' }} />
      </div>
    );
  }

  if (imageUrl && !hasError) {
    return (
      <div className={styles.imageContainer}>
        <img src={imageUrl} alt={itemName} className={styles.itemImage} onError={() => setHasError(true)} />
      </div>
    );
  }

  return (
    <div className={styles.imageContainer} style={{ background: `${accentColor}15` }}>
      <ImageOff size={32} color={accentColor} style={{ opacity: 0.5 }} />
    </div>
  );
};

const ItemDatabase: React.FC = () => {
  const [items, setItems] = useState<UniqueBuyableItem[]>([]);
  const [allCategories, setAllCategories] = useState<Array<{ category: string; count: number }>>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [versionInfo, setVersionInfo] = useState<{ scVersion: string; dataSource: string }>({ scVersion: 'Alpha', dataSource: 'LIVE' });
  
  // Filters
  const [primaryCategory, setPrimaryCategory] = useState<string>('');
  const [secondaryCategory, setSecondaryCategory] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(true);
  
  // Pagination
  const [skip, setSkip] = useState(0);
  const take = 51; // 3 columns x 17 rows for nice layout
  
  // Expanded items for details
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());

  // Compute available primary categories from data
  const primaryCategories = useMemo(() => {
    const primaries: Record<string, number> = {};
    
    allCategories.forEach(cat => {
      // Find which primary this belongs to
      for (const key of Object.keys(CATEGORY_STRUCTURE)) {
        if (cat.category.startsWith(key)) {
          primaries[key] = (primaries[key] || 0) + cat.count;
          break;
        }
      }
    });
    
    return Object.entries(primaries)
      .map(([key, count]) => ({ key, label: CATEGORY_STRUCTURE[key]?.label || key, count }))
      .sort((a, b) => b.count - a.count);
  }, [allCategories]);

  // Compute available secondary categories based on primary selection
  const secondaryCategories = useMemo(() => {
    if (!primaryCategory) return [];
    
    const config = CATEGORY_STRUCTURE[primaryCategory];
    if (!config || config.subcategories.length === 0) return [];
    
    // Filter allCategories to find matching subcategories
    return config.subcategories
      .map(sub => {
        const fullCategory = `${primaryCategory} - ${sub}`;
        const match = allCategories.find(c => c.category === fullCategory);
        return match ? { key: fullCategory, label: sub, count: match.count } : null;
      })
      .filter(Boolean) as { key: string; label: string; count: number }[];
  }, [primaryCategory, allCategories]);

  // Load categories on mount
  useEffect(() => {
    const loadCategories = async () => {
      try {
        const filterOptions = await personalItemService.getBuyableFilterOptions();
        setAllCategories(filterOptions.categories);
      } catch (err) {
        console.error('Failed to load categories:', err);
      }
    };
    loadCategories();
  }, []);

  // Load current SC version/data source (shared with Ship Database)
  useEffect(() => {
    const fetchVersion = async () => {
      try {
        const versionData = await shipService.getVersion();
        setVersionInfo(versionData);
      } catch (err) {
        console.error('Failed to fetch version:', err);
      }
    };
    fetchVersion();
  }, []);

  // Fetch items when filters change
  const fetchItems = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Use secondary category if selected, otherwise primary
      const categoryFilter = secondaryCategory || primaryCategory;

      const params: BuyableItemsParams = {
        ...(categoryFilter && { category: categoryFilter }),
        ...(searchQuery && { search: searchQuery }),
        skip,
        take
      };

      const data = await personalItemService.getUniqueBuyableItems(params);
      setItems(data);
      setTotalCount(data.length);
    } catch (err: any) {
      console.error('Failed to fetch items:', err);
      setError(err.response?.data?.error || 'Failed to load items');
    } finally {
      setLoading(false);
    }
  }, [primaryCategory, secondaryCategory, searchQuery, skip]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  // Clear secondary when primary changes
  useEffect(() => {
    setSecondaryCategory('');
    setSkip(0);
  }, [primaryCategory]);

  const handleClearFilters = () => {
    setPrimaryCategory('');
    setSecondaryCategory('');
    setSearchQuery('');
    setSkip(0);
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setSkip(0);
    fetchItems();
  };

  const toggleExpanded = (itemId: string) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(itemId)) {
      newExpanded.delete(itemId);
    } else {
      newExpanded.add(itemId);
    }
    setExpandedItems(newExpanded);
  };

  const hasActiveFilters = primaryCategory || secondaryCategory || searchQuery;

  const formatPrice = (price?: number) => {
    if (price === null || price === undefined) return 'N/A';
    return price.toLocaleString() + ' aUEC';
  };

  const dataSourceClass = versionInfo.dataSource?.toUpperCase() === 'LIVE'
    ? sharedStyles.dataSourceLive
    : sharedStyles.dataSourcePtu;

  return (
    <div className={sharedStyles.pageContainer}>
      <div className={sharedStyles.pageContent}>
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.titleRow}>
            <h1 className={styles.title}>
              <ShoppingBag size={28} />
              Item Database
            </h1>
            <span className={`${sharedStyles.dataSourceBadge} ${dataSourceClass}`}>{versionInfo.dataSource}</span>
          </div>
          <p className={styles.subtitle}>
            Browse in-game items available for purchase at shops across the verse
          </p>
        </div>

        {/* Stats Bar */}
        <div className={styles.statsBar}>
          <div className={styles.statCard}>
            <span className={styles.statLabel}>Items Loaded</span>
            <span className={styles.statValue}>{items.length}</span>
          </div>
          <div className={styles.statCard}>
            <span className={styles.statLabel}>Categories</span>
            <span className={styles.statValue}>{primaryCategories.length}</span>
          </div>
          {primaryCategory && (
            <div className={styles.statCard} style={{ borderColor: CATEGORY_STRUCTURE[primaryCategory]?.color }}>
              <span className={styles.statLabel}>Filtered by</span>
              <span className={styles.statValue} style={{ color: CATEGORY_STRUCTURE[primaryCategory]?.color }}>
                {secondaryCategory ? secondaryCategory.split(' - ')[1] : primaryCategory}
              </span>
            </div>
          )}
        </div>

        {/* Filter Toggle */}
        <button onClick={() => setShowFilters(!showFilters)} className={styles.filterToggle}>
          <Filter size={18} />
          {showFilters ? 'Hide Filters' : 'Show Filters'}
          {showFilters ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
        </button>

        {/* Filters */}
        {showFilters && (
          <div className={styles.filtersContainer}>
            <div className={styles.filtersGrid}>
              {/* Primary Category Filter */}
              <div className={styles.filterGroup}>
                <label className={styles.filterLabel}>
                  <Tag size={14} />
                  Category
                </label>
                <select
                  value={primaryCategory}
                  onChange={(e) => setPrimaryCategory(e.target.value)}
                  className={styles.filterSelect}
                >
                  <option value="">All Categories</option>
                  {primaryCategories.map(cat => (
                    <option key={cat.key} value={cat.key}>
                      {cat.label} ({cat.count})
                    </option>
                  ))}
                </select>
              </div>

              {/* Secondary Category Filter */}
              <div className={styles.filterGroup}>
                <label className={styles.filterLabel}>
                  <Filter size={14} />
                  Type
                </label>
                <select
                  value={secondaryCategory}
                  onChange={(e) => { setSecondaryCategory(e.target.value); setSkip(0); }}
                  className={styles.filterSelect}
                  disabled={!primaryCategory || secondaryCategories.length === 0}
                >
                  <option value="">All Types</option>
                  {secondaryCategories.map(cat => (
                    <option key={cat.key} value={cat.key}>
                      {cat.label} ({cat.count})
                    </option>
                  ))}
                </select>
              </div>

              {/* Search */}
              <div className={styles.filterGroup}>
                <label className={styles.filterLabel}>
                  <Search size={14} />
                  Search
                </label>
                <form onSubmit={handleSearch} className={styles.searchForm}>
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search items..."
                    className={styles.searchInput}
                  />
                </form>
              </div>
            </div>

            {/* Clear Filters */}
            {hasActiveFilters && (
              <button onClick={handleClearFilters} className={styles.clearButton}>
                <X size={16} />
                Clear Filters
              </button>
            )}
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className={styles.loadingState}>
            <div className={styles.spinner} />
            <p>Loading items...</p>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className={styles.errorState}>
            {error}
          </div>
        )}

        {/* Items Grid - 3 columns */}
        {!loading && !error && (
          <>
            <div className={styles.itemsGrid}>
              {items.map((item) => {
                const colors = getCategoryColor(item.category);
                const isExpanded = expandedItems.has(item.itemId);
                const displayName = item.displayName || item.itemId;

                return (
                  <div
                    key={item.itemId}
                    className={styles.itemCard}
                    style={{ '--accent-color': colors.accent } as React.CSSProperties}
                  >
                    {/* Image */}
                    <ItemImage itemName={displayName} accentColor={colors.accent} />

                    {/* Content */}
                    <div className={styles.itemContent}>
                      {/* Category Badge */}
                      <div className={styles.categoryBadge} style={{ background: colors.bg, color: colors.accent }}>
                        {item.category || 'Unknown'}
                      </div>

                      {/* Title */}
                      <h3 className={styles.itemTitle}>{displayName}</h3>

                      {/* Price */}
                      <div className={styles.priceRow}>
                        <div className={styles.priceValue}>
                          <DollarSign size={16} />
                          <span>
                            {item.minPrice === item.maxPrice 
                              ? formatPrice(item.minPrice)
                              : `${formatPrice(item.minPrice)} - ${formatPrice(item.maxPrice)}`
                            }
                          </span>
                        </div>
                        <div className={styles.shopCount}>
                          <Store size={14} />
                          <span>{item.shopCount} shop{item.shopCount !== 1 ? 's' : ''}</span>
                        </div>
                      </div>

                      {/* Expand button */}
                      <button onClick={() => toggleExpanded(item.itemId)} className={styles.expandButton}>
                        {isExpanded ? 'Hide Shops' : 'View Shops'}
                        {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                      </button>

                      {/* Expanded shop list */}
                      {isExpanded && item.shops && (
                        <div className={styles.shopList}>
                          {item.shops.slice(0, 8).map((shop, idx) => (
                            <div key={idx} className={styles.shopItem}>
                              <div className={styles.shopInfo}>
                                <span className={styles.shopName}>{shop.shopName}</span>
                                <span className={styles.shopLocation}>
                                  <MapPin size={10} />
                                  {shop.location || 'Unknown'}
                                </span>
                              </div>
                              <span className={styles.shopPrice}>{formatPrice(shop.price)}</span>
                            </div>
                          ))}
                          {item.shops.length > 8 && (
                            <div className={styles.moreShops}>
                              +{item.shops.length - 8} more shops
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Pagination */}
            {items.length > 0 && (
              <div className={styles.pagination}>
                <button
                  onClick={() => setSkip(Math.max(0, skip - take))}
                  disabled={skip === 0}
                  className={styles.pageButton}
                >
                  Previous
                </button>
                <span className={styles.pageInfo}>
                  Showing {skip + 1} - {skip + items.length}
                </span>
                <button
                  onClick={() => setSkip(skip + take)}
                  disabled={items.length < take}
                  className={styles.pageButton}
                >
                  Next
                </button>
              </div>
            )}

            {/* No results */}
            {items.length === 0 && (
              <div className={styles.emptyState}>
                <ShoppingBag size={48} />
                <p className={styles.emptyTitle}>No items found</p>
                <p className={styles.emptySubtitle}>Try adjusting your filters</p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ItemDatabase;
